import { CaregiverDto } from '@ecdlink/core';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localForage from 'localforage';
import {
  createCaregiver,
  getAllCaregiverClients,
  getCaregiverClients,
  getCaregiversForHealthCareWorker,
  updateCaregiver,
  upsertCareGivers,
} from './caregiver.actions';
import { CaregiverContactHistory, CaregiverState } from './caregiver.types';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';
import { Infant } from '@ecdlink/graphql';

const initialState: CaregiverState = {};

const caregiverSlice = createSlice({
  name: 'caregiver',
  initialState,
  reducers: {
    // Full wipe — must clear clients list too (used after a different user logs in)
    resetCaregiverState: () => initialState,
    clearSyncStatus: (state) => {
      if (state.caregivers) {
        for (let i = 0; i < state.caregivers.length; i++) {
          if (
            'synced' in state.caregivers[i] &&
            state.caregivers[i].synced === false
          ) {
            state.caregivers[i] = {
              ...state.caregivers[i],
              synced: true,
            };
          }
        }
      }
    },
    createCaregiver: (state, action: PayloadAction<CaregiverDto>) => {
      if (!state.caregivers) state.caregivers = [];

      if (!state.caregivers?.find((item) => item?.id === action.payload?.id)) {
        state.caregivers?.push(action.payload);
      }
    },
    updateCaregiver: (state, action: PayloadAction<CaregiverDto>) => {
      if (state.caregivers) {
        for (let i = 0; i < state.caregivers.length; i++) {
          if (state.caregivers[i].id === action.payload.id)
            state.caregivers[i] = action.payload;
        }
      }
    },
    updateCaregiverClientList: (state, action: PayloadAction<Infant>) => {
      if (!state.caregiverClientsList) return;

      const caregiver = state.caregiverClientsList.find(
        (c) => c.id === action.payload.caregiverId
      );

      if (!caregiver) return;

      // Ensure infants array exists
      if (!caregiver.infants) {
        caregiver.infants = [];
      }

      const index = caregiver.infants.findIndex(
        (infant) => infant && infant.id === action.payload.id
      );

      if (index === -1) {
        caregiver.infants.push(action.payload);
      }
    },
    updateCaregiverContactHistory: (
      state,
      action: PayloadAction<CaregiverContactHistory[]>
    ) => {
      state.contactHistory = action.payload;
    },
    addContactHistory: (
      state,
      action: PayloadAction<CaregiverContactHistory>
    ) => {
      if (!state.contactHistory) state.contactHistory = [];

      state.contactHistory.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, getCaregiverClients);
    setThunkActionStatus(builder, upsertCareGivers);
    builder.addCase(
      getCaregiversForHealthCareWorker.fulfilled,
      (state, action) => {
        if (!state.caregivers) {
          const caregivers = Object.assign(
            [],
            action.payload
          ) as CaregiverDto[];

          for (const element of caregivers) {
            element.isActive = true;
          }

          state.caregivers = caregivers;
        }
      }
    );
    builder.addCase(upsertCareGivers.fulfilled, (state, action) => {
      if (!state.caregivers) {
        const caregivers = Object.assign([], action.payload) as CaregiverDto[];

        for (const element of caregivers) {
          element.synced = true;
          element.isNew = false;
        }

        state.caregivers = caregivers;
      }
    });
    builder.addCase(
      updateCaregiver.fulfilled,
      (state, action: PayloadAction<CaregiverDto>) => {
        if (state.caregivers) {
          const caregiverIndex = state.caregivers.findIndex(
            (caregiver) => caregiver.id === action.payload.id
          );

          if (caregiverIndex < 0) return;

          state.caregivers[caregiverIndex] = action.payload;
        }
      }
    );
    builder.addCase(
      createCaregiver.fulfilled,
      (state, action: PayloadAction<CaregiverDto>) => {
        if (!state.caregivers) state.caregivers = [];
        state.caregivers
          ?.filter((item) => item?.id !== action.payload?.id)
          .push(action.payload);
      }
    );
    builder.addCase(getCaregiverClients.fulfilled, (state, action) => {
      setFulfilledThunkActionStatus(state, action);

      if (!!state.caregiverClientsList?.length) {
        if (
          !state.caregiverClientsList?.find(
            (item) => item?.id === action.payload?.id
          )
        ) {
          state.caregiverClientsList?.push(action.payload);
          return;
        }

        state.caregiverClientsList = state.caregiverClientsList.map(
          (caregiver) => {
            if (caregiver.id === action.payload.id) {
              return action.payload;
            } else {
              return caregiver;
            }
          }
        );
        return;
      }

      state.caregiverClientsList = [action.payload];
    });

    builder.addCase(getAllCaregiverClients.fulfilled, (state, action) => {
      state.caregiverClientsList = action.payload;
    });
  },
});

const { reducer: caregiverReducer, actions: caregiverActions } = caregiverSlice;

const caregiverPersistConfig = {
  key: 'caregiver',
  storage: localForage,
  blacklist: [],
};

export { caregiverPersistConfig, caregiverReducer, caregiverActions };
